import { useAppState } from '../context/OmniboxJsonConverterContext';
import { useState } from 'react';
import { updateCustomSetting, loadSettings, loadTechniqueSettings, saveSetting, loadRenamedColumns, saveRenamedColumns, getDisplayName } from './converterHelperFunctions';
import {
  Checkbox,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import DragHandleIcon from '@mui/icons-material/DragHandle';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

function RenderColumnsList() {
  const {
    globalColumns,
    selectedColumns,
    selectedSetting,
    detectedTechnique,
    searchQuery,
    setGlobalColumns,
    setSelectedColumns,
    setSelectedSetting
  } = useAppState();
  const [dragIdx, setDragIdx] = useState(null);
  const [columnNames, setColumnNames] = useState(loadRenamedColumns());
  const [editingCol, setEditingCol] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  if (!selectedColumns) {
    const savedTemplates = loadTechniqueSettings(detectedTechnique)['templates'];
    const customSetting = `${detectedTechnique}_custom`;
    const initialSetting = savedTemplates[customSetting] ? customSetting : detectedTechnique;
    setSelectedSetting(initialSetting);
    setSelectedColumns(savedTemplates[initialSetting]);
  }

  const displayOrder = [
    ...selectedColumns.filter(c => globalColumns.includes(c)),
    ...globalColumns.filter(c => !selectedColumns.includes(c))
  ].filter(c => c.toLowerCase().includes(searchQuery.toLowerCase()));


  const reorderList = (draggedCol, targetCol) => {
    const tempSelectedColumns = updateCustomSetting(
      selectedSetting,
      detectedTechnique,
      selectedColumns
    );

    if (tempSelectedColumns && tempSelectedColumns.length > 0) {
      const idx = tempSelectedColumns.indexOf(draggedCol);
      const newIdx = tempSelectedColumns.indexOf(targetCol);
      if (idx === -1 || newIdx === -1) return;

      tempSelectedColumns.splice(idx, 1);
      tempSelectedColumns.splice(newIdx, 0, draggedCol);

      if (detectedTechnique === selectedSetting) {
        saveSetting(detectedTechnique, `${selectedSetting}_custom`, tempSelectedColumns);
        setSelectedSetting(`${selectedSetting}_custom`);
      }
      else {
        saveSetting(detectedTechnique, selectedSetting, tempSelectedColumns);
      }
      setSelectedColumns(tempSelectedColumns);
    }
  }


  const moveItem = (col, direction) => () => {
    const tempSelectedColumns = updateCustomSetting(
      selectedSetting,
      detectedTechnique,
      selectedColumns
    );

    if (tempSelectedColumns) {
      // If the setting is not in unique Techniques, we can assume that the template being modified
      // is custom or user defined.
      // Otherwise, we just create and render the custom version of the template so the user can 
      // modify it from there - but we still need to apply the move itself either way.
      const idx = tempSelectedColumns.indexOf(col);
      if (idx === -1) return;

      const newIdx = Math.max(0, Math.min(tempSelectedColumns.length - 1, idx + direction));
      tempSelectedColumns.splice(idx, 1);
      tempSelectedColumns.splice(newIdx, 0, col);

      if (detectedTechnique === selectedSetting) {
        saveSetting(detectedTechnique, `${selectedSetting}_custom`, tempSelectedColumns);
        setSelectedSetting(`${selectedSetting}_custom`);
      }
      else {
        saveSetting(detectedTechnique, selectedSetting, tempSelectedColumns);
      }
      setSelectedColumns(tempSelectedColumns);
    }
  };


  const startEditingName = (col) => {
    setEditingCol(col);
    setEditingValue(getDisplayName(col, columnNames));
  };

  const commitEditingName = () => {
    if (editingCol !== null) {
      const updated = saveRenamedColumns(editingCol, editingValue);
      if (updated) setColumnNames(updated);
    }
    setEditingCol(null);
    setEditingValue('');
  };

  const cancelEditingName = () => {
    setEditingCol(null);
    setEditingValue('');
  };


  return (
    <List
      sx={{
        pt: '0', pb: '0',
        height: { xl: 320, lg: 240, md: 180, sm: 100 },
        overflow: 'auto'
      }}
    >
      {displayOrder.map((col) =>
        <ListItem
          key={col}
          draggable={selectedSetting !== '-' || searchQuery !== ''}
          onDragStart={(event) => { event.dataTransfer.setData('text/plain', col) }}
          onDragOver={(event) => { event.preventDefault(); }}
          onDrop={(event) => {
            event.preventDefault();
            const draggedCol = event.dataTransfer.getData('text/plain');
            const targetCol = col;
            if (selectedSetting !== '-') {
              reorderList(draggedCol, targetCol);
            }
            else {
              alert('Cannot modify \'default\' template.\nPlease select an existing template or add a new one.');
            }
          }}
          sx={{
            p: 0.75,
            display: 'flex', flexDirection: 'row',
            alignItems: 'center', justifyContent: 'space-between'
          }}
        >
          <Stack direction='row' spacing={1} alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
            <DragHandleIcon sx={{ cursor: 'grab' }} />
            <Checkbox
              size='small'
              checked={selectedColumns.includes(col)}
              onChange={(event) => {
                if (selectedSetting !== '-') {
                  const tempSelectedColumns = updateCustomSetting(
                    selectedSetting,
                    detectedTechnique,
                    selectedColumns
                  );
                  if (tempSelectedColumns) {
                    if (event.target.checked) {
                      if (!tempSelectedColumns.includes(col)) {
                        tempSelectedColumns.push(col);
                      }
                    }
                    else {
                      tempSelectedColumns.splice(tempSelectedColumns.indexOf(col), 1);
                    }
                    setSelectedColumns(tempSelectedColumns);
                    if (detectedTechnique === selectedSetting) {
                      saveSetting(detectedTechnique, `${selectedSetting}_custom`, tempSelectedColumns);
                      setSelectedSetting(`${selectedSetting}_custom`);
                    }
                    else {
                      saveSetting(detectedTechnique, selectedSetting, tempSelectedColumns);
                    }
                  }

                }
                else {
                  alert('Cannot modify \'default\' template.\nPlease select an existing template or add a new one.');
                }
              }}
              sx={{ height: '24px', width: '24px' }}
            />
            <IconButton
              size='small'
              sx={{ p: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (editingCol === col) {
                  commitEditingName();
                } else {
                  startEditingName(col);
                }
              }}
            >
              {editingCol === col ? <CheckIcon fontSize='small' /> : <EditIcon fontSize='small' />}
            </IconButton>
            {editingCol === col ? (
              <TextField
                size='small'
                autoFocus
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitEditingName();
                  if (e.key === 'Escape') cancelEditingName();
                }}
                onBlur={commitEditingName}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                draggable={false}
                inputProps={{ sx: { fontSize: '12px', py: 0.5 } }}
              />
            ) : (
              <Typography variant='caption' fontSize='12px' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getDisplayName(col, columnNames)}
              </Typography>
            )}
          </Stack>

          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <IconButton
              sx={{ p: '0' }}
              onClick={moveItem(col, -1)}
              disabled={selectedSetting === '-' || searchQuery !== ''}
            >
              <ArrowDropUpIcon />
            </IconButton>
            <IconButton
              sx={{ p: '0' }}
              onClick={moveItem(col, 1)}
              disabled={selectedSetting === '-' || searchQuery !== ''}
            >
              <ArrowDropDownIcon />
            </IconButton>
          </Stack>
        </ListItem>
      )}
    </List>
  )
}

export default RenderColumnsList;